import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Search, Loader2, FileText, Receipt, FolderKanban, Wrench, Shield, User } from 'lucide-react';

const TYPE_META = {
  contact: { icon: User, color: 'text-cyan-500', label: 'Contact', path: (r) => `/contacts/${r.id}` },
  estimate: { icon: FileText, color: 'text-blue-500', label: 'Estimate', path: () => '/estimates' },
  invoice: { icon: Receipt, color: 'text-emerald-500', label: 'Invoice', path: () => '/invoices' },
  project: { icon: FolderKanban, color: 'text-indigo-500', label: 'Project', path: (r) => `/projects/${r.id}` },
  job: { icon: Wrench, color: 'text-violet-500', label: 'Job', path: (r) => `/jobs/${r.id}` },
  permit: { icon: Shield, color: 'text-amber-500', label: 'Permit', path: () => '/permits' },
};

export default function GlobalSearch({ testId = 'global-search', autoFocus = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/search', { params: { q: q.trim() } });
        setResults(res.data?.results || []);
        setOpen(true);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 300);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q]);

  const goTo = (r) => {
    const meta = TYPE_META[r.type];
    if (!meta) return;
    setOpen(false);
    setQ('');
    if (r.type === 'estimate') navigate('/estimates', { state: { openEstimateId: r.id } });
    else if (r.type === 'invoice') navigate('/invoices', { state: { openInvoiceId: r.id } });
    else if (r.type === 'permit') navigate(`/permits?open=${r.id}`);
    else navigate(meta.path(r));
  };

  return (
    <div ref={boxRef} className="relative w-full">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      <Input
        data-testid={testId}
        value={q}
        autoFocus={autoFocus}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={t('header.searchPlaceholder')}
        className="pl-10 pr-8 bg-gray-50/80 border-gray-200/60 focus:bg-white h-9 text-sm"
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />}

      {open && q.trim().length >= 2 && (
        <div
          data-testid="global-search-results"
          className="absolute left-0 right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-xl z-50 max-h-96 overflow-y-auto animate-fade-in-up"
        >
          {results.length === 0 ? (
            <p data-testid="global-search-empty" className="px-4 py-6 text-center text-sm text-gray-400">
              {t('common.noResults', { defaultValue: 'No results' })}
            </p>
          ) : (
            results.map((r) => {
              const meta = TYPE_META[r.type] || TYPE_META.contact;
              const Icon = meta.icon;
              return (
                <button
                  key={`${r.type}-${r.id}`}
                  type="button"
                  data-testid={`global-search-result-${r.type}-${r.id}`}
                  onClick={() => goTo(r)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left transition-colors border-b border-gray-50 last:border-0"
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${meta.color}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 truncate">{r.label || '—'}</span>
                    {r.sublabel && <span className="block text-xs text-gray-500 truncate">{r.sublabel}</span>}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 flex-shrink-0">{meta.label}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
